"use client"

import { RefreshCw, RotateCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useShellTranslation } from "@/lib/shell/use-shell-translation"

interface LiveIndicatorProps {
  /** Polling is on: the table refetches by itself every `pollMs`. */
  active: boolean
  pollMs: number
  /**
   * A fetch is in flight right now. Only the icon spins for it; the wording
   * follows `active` (PROD-888: tying the wording to the fetch made a live
   * table read "Live updates off" between every poll). Omitted, the icon
   * spins while `active`, as it always did.
   */
  fetching?: boolean
  /** Said instead of "Live updates off" when the consumer knows why polling stopped. */
  pausedLabel?: string
  /** When given, a "Refresh now" button sits beside the pill. */
  onRefresh?: () => void
  className?: string
}

/**
 * Small pill in a live table's toolbar. Shows refresh state
 * + cadence so users know the table is live without needing to F5.
 * Worded in the interface language (English outside the provider).
 */
export function LiveIndicator({
  active,
  pollMs,
  fetching,
  pausedLabel,
  onRefresh,
  className,
}: LiveIndicatorProps) {
  const { t } = useShellTranslation()
  const seconds = Math.round(pollMs / 1000)
  const spinning = fetching ?? active
  const pill = (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
        active
          ? "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300"
          : "border-muted bg-muted text-muted-foreground",
        className,
      )}
      aria-live="polite"
    >
      <RefreshCw
        className={cn("h-3 w-3", spinning && "animate-spin [animation-duration:2s]")}
        aria-hidden
      />
      {active ? t("shell.live.on", { seconds }) : (pausedLabel ?? t("shell.live.off"))}
    </div>
  )
  if (!onRefresh) return pill
  return (
    <div className="inline-flex items-center gap-1">
      {pill}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={onRefresh}
        disabled={fetching}
        aria-label={t("shell.live.refreshNow")}
        title={t("shell.live.refreshNow")}
      >
        <RotateCw className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </div>
  )
}
